import React, {
  createContext,
  useContext,
  useState,
  useRef,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import { useInRouterContext, useLocation } from 'react-router-dom';
import ConfirmDialog from '../components/ConfirmDialog';

const ConfirmContext = createContext(null);

export const useConfirm = () => {
  const context = useContext(ConfirmContext);
  if (!context) {
    throw new Error('useConfirm must be used within a ConfirmProvider');
  }
  // Reason: a stable per-caller identity so the provider can tell whose dialog is
  // open and dismiss it when that caller unmounts (route change) instead of
  // leaving it overlaid on the next page.
  const [token] = useState(() => ({}));
  const { request, dismiss } = context;

  useEffect(() => () => dismiss(token), [dismiss, token]);

  return useCallback((opts) => request(token, opts), [request, token]);
};

// Reason: the cleanup runs when `location.key` changes — i.e. on any push,
// replace or back/forward — which is the only reliable signal that navigation
// happened while a dialog might be open. Owner unmount is not enough: React
// Router reuses the same ArticleViewer/TopicDetail instance across an id change,
// so a dialog opened for article B would otherwise stay over article A.
const RouteChangeDismiss = ({ onRouteChange }) => {
  const location = useLocation();
  useEffect(() => () => onRouteChange(), [location.key, onRouteChange]);
  return null;
};

export const ConfirmProvider = ({ children }) => {
  const [options, setOptions] = useState(null);
  // Reason: a ref, not state — two confirm() calls in one handler both read a
  // stale `options`, so only a ref catches the second one.
  const pendingRef = useRef(null);
  const ownerRef = useRef(null);
  const restoreFocusRef = useRef(null);

  const request = useCallback((owner, opts) => {
    if (pendingRef.current) {
      return Promise.reject(new Error('confirm() called while a dialog is already open'));
    }
    restoreFocusRef.current = document.activeElement;
    return new Promise((resolve) => {
      pendingRef.current = resolve;
      ownerRef.current = owner;
      setOptions(opts || {});
    });
  }, []);

  const handleResolve = useCallback((result) => {
    const resolve = pendingRef.current;
    if (!resolve) return;
    const previous = restoreFocusRef.current;
    pendingRef.current = null;
    ownerRef.current = null;
    restoreFocusRef.current = null;
    setOptions(null);
    resolve(result);
    // Reason: focus() runs last, after all provider state is settled, so anything
    // it triggers (a re-entrant confirm(), an owner unmount -> dismiss()) sees a
    // clean provider instead of re-entering a half-finished close. setOptions(null)
    // is batched, so the dialog is still in the DOM while focus moves back and
    // never falls to <body>. focus() may not throw in practice, but nothing after
    // it depends on it.
    try {
      if (previous && typeof previous.focus === 'function') previous.focus();
    } catch {
      /* ignore */
    }
  }, []);

  const dismiss = useCallback(
    (owner) => {
      if (!pendingRef.current || ownerRef.current !== owner) return;
      handleResolve(false);
    },
    [handleResolve]
  );

  // Reason: unlike dismiss(), this ignores the owner — after a route change the
  // owner may still be mounted (a reused route component) but its dialog no
  // longer belongs to the page on screen.
  const dismissAll = useCallback(() => {
    handleResolve(false);
  }, [handleResolve]);

  const value = useMemo(() => ({ request, dismiss }), [request, dismiss]);

  // Reason: tests (and any future non-routed mount) render the provider without
  // a Router; useLocation() would throw there, so only mount the watcher when a
  // router context exists. useInRouterContext is a hook but is called
  // unconditionally here.
  const inRouter = useInRouterContext();

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      {inRouter && <RouteChangeDismiss onRouteChange={dismissAll} />}
      {options && (
        <ConfirmDialog
          title={options.title}
          message={options.message}
          emoji={options.emoji}
          danger={options.danger}
          confirmLabel={options.confirmLabel ?? 'Confirm'}
          cancelLabel={options.cancelLabel ?? 'Cancel'}
          onResolve={handleResolve}
        />
      )}
    </ConfirmContext.Provider>
  );
};
